import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { formatJMD } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Countdown } from "@/components/Countdown";
import { Truck, MapPin, CreditCard, Landmark, Zap } from "lucide-react";

const PARISHES = [
  "Kingston", "St. Andrew", "St. Catherine", "Clarendon", "Manchester",
  "St. Elizabeth", "Westmoreland", "Hanover", "St. James", "Trelawny",
  "St. Ann", "St. Mary", "Portland", "St. Thomas",
];

const DELIVERY = [
  { value: "knutsford_islandwide", name: "KNUTSFORD EXPRESS (ISLAND-WIDE)", desc: "Drop-off at nearest Knutsford Express location · 1-2 business days", cost: 70000 },
  { value: "zipmail_postoffice", name: "ZIPMAIL (TO POST OFFICE)", desc: "Collect at your nearest Post Office · 2-4 business days", cost: 50000 },
];

const providerIcon = (p) => (p.instant ? CreditCard : Landmark);

export default function Checkout() {
  const { items, subtotal, clear } = useCart();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [providers, setProviders] = useState([]);
  const [promo, setPromo] = useState(null);
  const [fees, setFees] = useState({});
  const [savedCards, setSavedCards] = useState([]); // populated only when gateway live + customer logged in
  const [selectedCardToken, setSelectedCardToken] = useState(null);
  const [card, setCard] = useState({ number: "", expiry: "", cvv: "", save: false });
  const [form, setForm] = useState({
    full_name: "", phone: "", email: "", whatsapp: "", parish: "", address: "",
    delivery_method: "", payment_method: "",
  });

  useEffect(() => {
    api.get("/payment-providers").then((r) => {
      setProviders(r.data);
      if (r.data.length) setForm((f) => ({ ...f, payment_method: f.payment_method || r.data[0].id }));
    }).catch(() => {});
    api.get("/promo").then((r) => setPromo(r.data)).catch(() => {});
    api.get("/delivery-fees").then((r) => setFees(r.data)).catch(() => {});
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const deliveryOptions = DELIVERY.map((d) => ({ ...d, cost: fees[d.value] ?? d.cost }));
  const selectedDelivery = deliveryOptions.find((d) => d.value === form.delivery_method);
  const itemCount = items.reduce((s, i) => s + i.qty, 0);
  const saleActive = !!promo?.active;
  const discount = saleActive ? Math.round(subtotal * promo.sale_percent / 100) : 0;
  const freeShip = saleActive && itemCount >= promo.free_ship_min_items;
  const deliveryCost = freeShip ? 0 : (selectedDelivery ? selectedDelivery.cost : 0);
  const total = subtotal - discount + deliveryCost;
  const activeProvider = providers.find((p) => p.id === form.payment_method);
  const isInstant = !!activeProvider?.instant;

  if (items.length === 0 && !submitting) {
    return (
      <div className="pt-32 pb-24 text-center">
        <p className="font-display text-4xl">NUTTEN INNA DI BAG</p>
        <Button onClick={() => navigate("/shop")} className="mt-6 bg-jamaica-gold text-black rounded-none uppercase font-bold">Shop</Button>
      </div>
    );
  }

  const validate = () => {
    if (!form.full_name.trim()) return "Enter your full name";
    if (!form.phone.trim()) return "Enter your phone number";
    if (!form.parish) return "Select your parish";
    if (!form.delivery_method) return "Choose a delivery method";
    if (!form.address.trim()) return "Enter your delivery address";
    if (!form.email.trim() && !form.whatsapp.trim()) return "Add an email or WhatsApp for order updates";
    if (!form.payment_method) return "Choose a payment method";
    if (isInstant && !selectedCardToken) {
      if (!card.number.trim() || !card.expiry.trim() || !card.cvv.trim()) return "Enter your card details";
    }
    return null;
  };

  const submit = async () => {
    const err = validate();
    if (err) { toast.error(err); return; }
    setSubmitting(true);
    // For a live card gateway, FAC tokenizes the card client-side and returns a
    // token — only that token is sent. Raw card numbers never reach our server.
    const payload = {
      items: items.map((i) => ({ product_id: i.product_id, size: i.size, qty: i.qty })),
      customer: {
        full_name: form.full_name, phone: form.phone, email: form.email,
        whatsapp: form.whatsapp, parish: form.parish, address: form.address,
        delivery_method: form.delivery_method,
      },
      payment_method: form.payment_method,
      card_token: selectedCardToken || null,
      save_card: isInstant ? card.save : false,
    };
    try {
      const r = await api.post("/orders", payload);
      clear();
      navigate("/order-confirmation", { state: { order: r.data } });
    } catch (e) {
      toast.error(e?.response?.data?.detail || (isInstant ? "Payment declined. Try another card." : "Order failed. Try again."));
      setSubmitting(false);
    }
  };

  const field = "bg-card border-border rounded-none h-11 focus-visible:ring-jamaica-gold";
  const cardField = "bg-background border-border rounded-none h-11 focus-visible:ring-jamaica-gold font-mono";

  return (
    <div data-testid="checkout-page" className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-16">
      <h1 className="font-display text-5xl tracking-tight mb-8">CHECKOUT</h1>
      <div className="grid lg:grid-cols-[1fr_360px] gap-8">
        <div className="space-y-8">
          {/* CONTACT */}
          <section>
            <h2 className="font-heading font-bold uppercase text-sm mb-4 flex items-center gap-2"><MapPin className="w-4 h-4 text-jamaica-gold" /> Delivery Details</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <Label className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Full Name *</Label>
                <Input data-testid="checkout-name" value={form.full_name} onChange={(e) => set("full_name", e.target.value)} className={field} placeholder="Andre Campbell" />
              </div>
              <div>
                <Label className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Phone *</Label>
                <Input data-testid="checkout-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} className={field} placeholder="876-000-0000" />
              </div>
              <div>
                <Label className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">WhatsApp</Label>
                <Input data-testid="checkout-whatsapp" value={form.whatsapp} onChange={(e) => set("whatsapp", e.target.value)} className={field} placeholder="876-000-0000" />
              </div>
              <div className="sm:col-span-2">
                <Label className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Email (for order updates)</Label>
                <Input data-testid="checkout-email" value={form.email} onChange={(e) => set("email", e.target.value)} className={field} placeholder="you@email.com" />
              </div>
              <div>
                <Label className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Parish *</Label>
                <Select value={form.parish} onValueChange={(v) => set("parish", v)}>
                  <SelectTrigger data-testid="checkout-parish" className={field}><SelectValue placeholder="Select parish" /></SelectTrigger>
                  <SelectContent className="bg-popover border-border">
                    {PARISHES.map((p) => <SelectItem key={p} value={p} data-testid={`parish-${p}`}>{p}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="sm:col-span-2">
                <Label className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Delivery Address *</Label>
                <Textarea data-testid="checkout-address" value={form.address} onChange={(e) => set("address", e.target.value)} className="bg-card border-border rounded-none focus-visible:ring-jamaica-gold" placeholder="Street, town, landmark" />
              </div>
            </div>
          </section>

          {/* DELIVERY METHOD */}
          <section>
            <h2 className="font-heading font-bold uppercase text-sm mb-4 flex items-center gap-2"><Truck className="w-4 h-4 text-jamaica-gold" /> Delivery Method</h2>
            <div className="grid sm:grid-cols-2 gap-3">
              {deliveryOptions.map((d) => (
                <button
                  key={d.value}
                  data-testid={`delivery-${d.value}`}
                  onClick={() => set("delivery_method", d.value)}
                  className={`text-left border p-4 transition-colors ${form.delivery_method === d.value ? "border-jamaica-gold bg-jamaica-gold/5" : "border-border hover:border-white/30"}`}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-heading font-bold uppercase text-sm">{d.name}</span>
                    <span className="font-mono text-xs text-jamaica-gold">{formatJMD(d.cost)}</span>
                  </div>
                  <p className="text-xs text-zinc-500 mt-1">{d.desc}</p>
                </button>
              ))}
            </div>
          </section>

          {/* PAYMENT */}
          <section>
            <h2 className="font-heading font-bold uppercase text-sm mb-4 flex items-center gap-2"><CreditCard className="w-4 h-4 text-jamaica-gold" /> Payment</h2>
            <div className="grid sm:grid-cols-2 gap-3" data-testid="payment-providers">
              {providers.map((p) => {
                const Icon = providerIcon(p);
                return (
                  <button
                    key={p.id}
                    data-testid={`payment-${p.id}`}
                    onClick={() => { set("payment_method", p.id); setSelectedCardToken(null); }}
                    className={`text-left border p-4 transition-colors ${form.payment_method === p.id ? "border-jamaica-gold bg-jamaica-gold/5" : "border-border hover:border-white/30"}`}
                  >
                    <span className="font-heading font-bold uppercase text-sm flex items-center gap-2">
                      <Icon className="w-4 h-4" /> {p.label}
                      {p.instant && <span className="flex items-center gap-1 text-[9px] text-jamaica-green"><Zap className="w-3 h-3" />INSTANT</span>}
                    </span>
                    <p className="text-xs text-zinc-500 mt-1">{p.description}</p>
                  </button>
                );
              })}
            </div>

            {/* CARD FORM — rendered automatically once an instant (card) provider is active */}
            {isInstant && (
              <div data-testid="card-payment-form" className="border border-jamaica-gold/30 bg-card p-4 mt-3 space-y-4">
                {savedCards.length > 0 && (
                  <div className="space-y-2" data-testid="saved-cards">
                    <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Your Saved Cards</p>
                    {savedCards.map((sc) => (
                      <button
                        key={sc.token_reference}
                        data-testid={`saved-card-${sc.last4}`}
                        onClick={() => setSelectedCardToken(sc.token_reference)}
                        className={`w-full text-left border p-3 flex items-center justify-between transition-colors ${selectedCardToken === sc.token_reference ? "border-jamaica-gold bg-jamaica-gold/5" : "border-border hover:border-white/30"}`}
                      >
                        <span className="font-mono text-sm">{sc.card_brand} ending in {sc.last4}</span>
                        <span className="font-mono text-xs text-zinc-500">{sc.expiry}</span>
                      </button>
                    ))}
                    <button data-testid="use-new-card" onClick={() => setSelectedCardToken(null)} className="font-mono text-[10px] uppercase tracking-widest text-jamaica-gold">+ Use a new card</button>
                  </div>
                )}

                {!selectedCardToken && (
                  <div className="space-y-3">
                    <div>
                      <Label className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Card Number</Label>
                      <Input data-testid="card-number" inputMode="numeric" value={card.number} onChange={(e) => setCard({ ...card, number: e.target.value })} className={cardField} placeholder="4242 4242 4242 4242" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Expiry</Label>
                        <Input data-testid="card-expiry" value={card.expiry} onChange={(e) => setCard({ ...card, expiry: e.target.value })} className={cardField} placeholder="MM/YY" />
                      </div>
                      <div>
                        <Label className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">CVV</Label>
                        <Input data-testid="card-cvv" inputMode="numeric" value={card.cvv} onChange={(e) => setCard({ ...card, cvv: e.target.value })} className={cardField} placeholder="123" />
                      </div>
                    </div>
                    <label className="flex items-center gap-2 pt-1">
                      <Checkbox data-testid="save-card" checked={card.save} onCheckedChange={(v) => setCard({ ...card, save: !!v })} />
                      <span className="font-mono text-xs text-zinc-400">Save this card for faster checkout next time</span>
                    </label>
                    <p className="text-[10px] text-zinc-600 leading-relaxed">Card details are encrypted and tokenized by Sagicor's gateway. We never see or store your card number.</p>
                  </div>
                )}
              </div>
            )}

            {!isInstant && (
              <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-600 mt-3 border border-dashed border-white/10 p-3">
                ⚙︎ Pay by bank transfer, then upload your receipt on the next screen — we confirm and ship once it lands.
              </p>
            )}
          </section>
        </div>

        {/* SUMMARY */}
        <div className="border border-white/10 bg-card p-5 h-fit lg:sticky lg:top-24">
          <h2 className="font-heading font-bold uppercase text-sm mb-4">Order Summary</h2>
          <div className="space-y-3 max-h-64 overflow-y-auto mb-4">
            {items.map((i, idx) => (
              <div key={idx} className="flex gap-3">
                <img src={i.image} alt={i.title} className="w-12 h-14 object-cover bg-zinc-900" />
                <div className="flex-1 min-w-0">
                  <p className="font-heading font-bold uppercase text-[11px] leading-tight truncate">{i.title}</p>
                  <p className="font-mono text-[10px] text-zinc-500 uppercase">{i.size} × {i.qty}</p>
                </div>
                <span className="font-mono text-xs text-jamaica-gold">{formatJMD(i.price_cents * i.qty)}</span>
              </div>
            ))}
          </div>
          <div className="border-t border-white/10 pt-3 space-y-2 font-mono text-sm">
            <div className="flex justify-between"><span className="text-zinc-400 text-xs uppercase tracking-widest">Subtotal</span><span>{formatJMD(subtotal)}</span></div>
            {discount > 0 && <div className="flex justify-between text-jamaica-green"><span className="text-xs uppercase tracking-widest" data-testid="checkout-discount-label">Sale {promo.sale_percent}% Off</span><span data-testid="checkout-discount">−{formatJMD(discount)}</span></div>}
            <div className="flex justify-between"><span className="text-zinc-400 text-xs uppercase tracking-widest">Delivery</span><span data-testid="checkout-delivery">{deliveryCost === 0 ? "FREE" : formatJMD(deliveryCost)}</span></div>
            {saleActive && !freeShip && itemCount > 0 && <p className="text-[10px] text-jamaica-gold" data-testid="checkout-freeship-hint">Add {promo.free_ship_min_items - itemCount} more item(s) for FREE shipping</p>}
            {saleActive && promo.ends_at && <div className="pt-2"><Countdown endsAt={promo.ends_at} /></div>}
            <div className="flex justify-between text-base pt-2 border-t border-white/10"><span className="uppercase tracking-widest text-xs self-center">Total</span><span className="text-jamaica-gold" data-testid="checkout-total">{formatJMD(total)}</span></div>
          </div>
          <Button
            data-testid="place-order-btn"
            onClick={submit}
            disabled={submitting || !form.delivery_method}
            className="w-full mt-5 py-6 bg-jamaica-gold hover:bg-yellow-300 text-black font-bold uppercase tracking-widest rounded-none disabled:opacity-40"
          >
            {submitting ? (isInstant ? "Processing Payment…" : "Placing…") : (isInstant ? `Pay ${formatJMD(total)}` : "Complete Order")}
          </Button>
          <p className="text-[11px] text-zinc-500 mt-3 leading-relaxed">
            {isInstant
              ? "Your card is charged instantly and securely. Tracking is sent once your order ships."
              : "Orders are confirmed once payment is received. Delivery details and tracking will be sent after payment and dispatch."}
          </p>
        </div>
      </div>
    </div>
  );
}
